import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Plus, Camera, Mic, Layers, Calendar, ClipboardCheck, Star, Search as SearchIcon, RefreshCw } from 'lucide-react'
import { api } from '../../lib/api.js'
import { fmtClock } from '../../lib/date.js'
import { toast } from '../../components/feedback/Toast.jsx'
import { fmtDay, fmtEditedDay, localDayOf, useCanEdit, canTouch, primeKeyboard } from './lib.js'
import SwipeRow from '../../components/SwipeRow.jsx'
import Initials from './Initials.jsx'
import { PushNudge } from '../../components/PushSettings.jsx'

/**
 * Session Log home — one list, everything is a session (Charles,
 * 2026-08-01: no project/session divide in the list). Multi-day sessions
 * are the old "projects" wearing a stacked-days indicator; swipe a row
 * left to delete it.
 */
// When the calendar was last REALLY re-read (module-level so it survives
// route remounts — the throttle is per app session, not per mount).
let lastFreshAt = 0

export default function ProjectsPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const me = useQuery({ queryKey: ['me'], queryFn: () => api('/auth/me'), staleTime: Infinity })
  const { data, isLoading } = useQuery({ queryKey: ['sl-projects'], queryFn: () => api('/log/projects') })
  // Today's studio-calendar bookings not logged yet (the section renders
  // only when a calendar feed is configured). Bookings change during the
  // day, so this refetches when the app comes back to the foreground and
  // every 5 minutes while it's open (Charles, 2026-08-03: events weren't
  // showing consistently) — the global refetchOnWindowFocus:false is for
  // content that doesn't move; this does.
  const suggested = useQuery({
    queryKey: ['sl-suggestions'],
    queryFn: () => api('/log/suggestions'),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
    refetchInterval: 5 * 60 * 1000,
  })
  const [refreshing, setRefreshing] = useState(false)
  // Sort order is a per-device preference (Charles, 2026-08-26): display
  // only, nothing stored server-side. Reading localStorage can throw in a
  // locked-down browser, so it's guarded both ways.
  const [sortBy, setSortBy] = useState(() => {
    try {
      return localStorage.getItem('sl-sort') === 'name' ? 'name' : 'edited'
    } catch {
      return 'edited'
    }
  })
  const chooseSort = (next) => {
    setSortBy(next)
    try {
      localStorage.setItem('sl-sort', next)
    } catch {
      /* private mode — the choice just won't survive a relaunch */
    }
  }

  // Arriving here runs the same real re-read as the Refresh tap (Charles,
  // 2026-08-05: "I don't know that everyone will do that") — the cached
  // list paints instantly and the spinner shows the check happening.
  // Throttled: within 2 minutes of the last real read, arriving again is
  // a no-op — the focus/interval refetches carry the in-between.
  useEffect(() => {
    if (Date.now() - lastFreshAt < 2 * 60 * 1000) return
    refreshSuggestions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Admin accountability row (Charles, 2026-08-04): bookings from the last
  // 14 days with no matching log. Server answers non-admins with an empty
  // list, so the row simply never renders for them.
  const isAdmin = me.data?.user?.role === 'admin'
  const check = useQuery({
    queryKey: ['sl-check'],
    queryFn: () => api('/log/check'),
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
  const checkCount = (check.data?.days || []).reduce((n, d) => n + d.deficit, 0)

  // Social media digest (Charles, 2026-09-01) — admin-only, like the check.
  // Shares the ['sl-social'] cache with the digest screen, so the count here
  // and the page it opens are the same fetch.
  const social = useQuery({
    queryKey: ['sl-social'],
    queryFn: () => api('/log/social'),
    enabled: isAdmin,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  })
  const socialCount = (social.data?.entries || []).length

  // The Refresh tap is for "I just put it on the calendar, where is it" —
  // it bypasses the server's ICS cache too, so it really re-reads Google.
  async function refreshSuggestions() {
    lastFreshAt = Date.now()
    setRefreshing(true)
    try {
      qc.setQueryData(['sl-suggestions'], await api('/log/suggestions?fresh=1'))
    } catch {
      toast('Couldn’t reach the calendar', 'error')
    } finally {
      setRefreshing(false)
    }
  }
  const canEdit = useCanEdit(me)

  // Search over everything logged — gear, notes, chains, names. Typing
  // swaps the screen below the field for results; clearing brings the
  // home feed back. Same debounce idiom as the Information search.
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 150)
    return () => clearTimeout(t)
  }, [q])
  const searching = debounced.trim().length > 0
  const search = useQuery({
    queryKey: ['sl-search', debounced],
    queryFn: () => api(`/log/search?q=${encodeURIComponent(debounced)}`),
    enabled: searching,
    placeholderData: (prev) => prev, // keep old results while typing
  })

  async function newMultiday() {
    const name = window.prompt('Multi-day session name:')
    if (!name?.trim()) return
    try {
      const res = await api('/log/projects', { body: { name: name.trim() } })
      qc.invalidateQueries({ queryKey: ['sl-projects'] })
      primeKeyboard()
      navigate(`/log/${res.id}/new`) // straight into day 1
    } catch (err) {
      toast(err.message || 'Couldn’t create', 'error')
    }
  }

  async function deleteItem(item) {
    const what = item.multi ? `“${item.name}” and all its days` : `“${item.label || 'Session'}”`
    if (!window.confirm(`Delete ${what}?`)) return
    try {
      await api(item.multi ? `/log/projects/${item.id}` : `/log/sessions/${item.id}`, { method: 'DELETE' })
      qc.invalidateQueries({ queryKey: ['sl-projects'] })
    } catch (err) {
      toast(err.message || 'Couldn’t delete', 'error')
    }
  }

  // One feed: standalone sessions and multi-day sessions interleaved.
  const items = [
    // `sort` is the recency key (Charles, 2026-08-05: most recently EDITED
    // first — the log you just touched is the one you'll reach for again);
    // `name` is the alphabetical key, so a standalone session sorts by the
    // same text its row displays.
    ...(data?.standalone || []).map((s) => ({
      ...s,
      multi: false,
      sort: s.updatedAt || s.date || '',
      name: s.label || 'Session',
    })),
    ...(data?.projects || []).map((p) => ({
      ...p,
      multi: true,
      sort: p.updatedAt || p.lastDate || (p.createdAt || '').slice(0, 10),
    })),
  ].sort(
    sortBy === 'name'
      ? // numeric so "Day 2" precedes "Day 10"; base sensitivity so case
        // and accents don't split the alphabet.
        (a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base', numeric: true })
      : (a, b) => b.sort.localeCompare(a.sort)
  )

  return (
    <div>
      <div className="mb-4 border-b border-hairline pb-3">
        <h1 className="text-2xl font-semibold text-ink">Session Log</h1>
      </div>

      {/* Between the title and New session (Charles, 2026-08-03): the
          answer to "when did we last use the U87" lives here. */}
      <div className="relative mb-5">
        <SearchIcon size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-faint" />
        <input
          type="search"
          placeholder="Search sessions, gear, notes…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="tap w-full rounded-2xl border border-hairline bg-white py-3 pl-11 pr-4 text-[16px] text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-link"
        />
      </div>

      {!searching && canEdit && (
        <div className="mb-5 space-y-2">
          <button
            onClick={() => { primeKeyboard(); navigate('/log/new') }}
            className="tap flex w-full items-center justify-center gap-2 rounded-xl bg-navy px-4 py-3 text-sm font-medium text-white"
          >
            <Plus size={16} /> New Session Log
          </button>
          <button
            onClick={newMultiday}
            className="tap flex w-full items-center justify-center gap-2 rounded-xl border-ink px-4 py-2.5 text-sm font-medium text-ink"
            style={{ borderWidth: '1.5px' }}
          >
            <Layers size={15} /> New multi-day session
          </button>
        </div>
      )}

      {/* Today's bookings from the studio calendar, one tap from a logged
          session — right under the New buttons so they're the first thing
          seen on a session day (Charles, 2026-08-02). Already-logged titles
          are filtered server-side, so this empties as the day gets logged;
          the hairline below fences it off from the existing sessions. The
          section shows whenever a feed is configured — even empty — so the
          Refresh tap is there exactly when an expected booking is missing. */}
      {!searching && canEdit && suggested.data?.configured && (
        <section className="mb-5 border-b border-hairline pb-5">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="section-header">Suggested · today’s calendar</h2>
            <button
              onClick={refreshSuggestions}
              disabled={refreshing}
              className="tap flex items-center gap-1 text-xs font-medium text-ink underline underline-offset-2 disabled:opacity-50"
            >
              <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Checking…' : 'Refresh'}
            </button>
          </div>
          {(suggested.data.suggestions?.length ?? 0) === 0 ? (
            <p className="text-xs text-faint">No unlogged bookings on today’s calendar.</p>
          ) : (
          <div className="card divide-y divide-hairline">
            {suggested.data.suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => {
                  primeKeyboard()
                  // The booking's delivery details ride along into the form,
                  // plus what the title gives away: the room marker and
                  // publisher-name → Audiobook (Charles, 2026-08-08).
                  // `title` is the raw booking (it becomes calendar_title, the
                  // fingerprint that hides this row afterwards); `label` is
                  // what lands in the name field — the booking without its
                  // "(1)"/"(A)" room marker (Charles, 2026-08-29).
                  const qs = new URLSearchParams({ title: s.title, label: s.label || s.title })
                  if (s.editGoesTo) qs.set('editGoesTo', s.editGoesTo)
                  if (s.narrators) qs.set('narrators', s.narrators)
                  if (s.room) qs.set('room', s.room)
                  if (s.sessionType) qs.set('sessionType', s.sessionType)
                  navigate(`/log/new?${qs.toString()}`)
                }}
                className="tap flex w-full items-center gap-3 px-4 py-3 text-left active:bg-grouped"
              >
                <Calendar size={17} className="shrink-0 text-cyan" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">{s.title}</div>
                  {(s.time || s.narrators) && (
                    <div className="mt-0.5 truncate text-xs text-muted">
                      {[s.time && fmtClock(s.time), s.narrators].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
                <Plus size={16} className="shrink-0 text-faint" />
              </button>
            ))}
          </div>
          )}
        </section>
      )}

      {/* One-line opt-in for push reminders (Charles, 2026-09-11) — engineers
          never visit More, so this is where the offer has to sit. Renders
          only while this device could opt in and hasn't; X hides it. */}
      {!searching && canEdit && <PushNudge />}

      {/* Admin-only rows, grouped as one card so they read as a bench of
          management views rather than two loose sections: the accountability
          check (quiet when all clear; a count when bookings from the last 14
          days have no matching log) and the social-media digest. */}
      {!searching && isAdmin && (
        <div className="card mb-5 divide-y divide-hairline">
          {check.data?.configured && (
            <Link to="/log/check" className="tap flex items-center gap-3 px-4 py-3 active:bg-grouped">
              <ClipboardCheck size={17} className="shrink-0 text-ink" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">Log check</span>
              <span className={'shrink-0 text-xs ' + (checkCount > 0 ? 'font-semibold text-ink' : 'text-faint')}>
                {checkCount > 0 ? `${checkCount} to review` : 'all clear'}
              </span>
              <ChevronRight size={16} className="shrink-0 text-faint" />
            </Link>
          )}
          <Link to="/log/social" className="tap flex items-center gap-3 px-4 py-3 active:bg-grouped">
            <Star size={17} className="shrink-0 text-ink" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">Social media</span>
            <span className={'shrink-0 text-xs ' + (socialCount > 0 ? 'font-semibold text-ink' : 'text-faint')}>
              {socialCount > 0 ? `${socialCount} flagged` : 'none flagged'}
            </span>
            <ChevronRight size={16} className="shrink-0 text-faint" />
          </Link>
        </div>
      )}

      {/* Sort toggle — recency is the default because it answers "what was
          I just doing"; A–Z answers "where is that one project" once the
          list is long (Charles, 2026-08-26). */}
      {!searching && !isLoading && items.length > 1 && (
        <div className="mb-2 flex items-center justify-end gap-2 text-xs">
          <span className="text-faint">Sort</span>
          {[
            ['edited', 'Recent'],
            ['name', 'A–Z'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => chooseSort(key)}
              aria-pressed={sortBy === key}
              className={
                'tap ' +
                (sortBy === key
                  ? 'font-medium text-ink underline underline-offset-2'
                  : 'text-muted active:text-ink')
              }
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {searching ? (
        <SearchResults search={search} q={debounced} />
      ) : isLoading ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : items.length === 0 ? (
        <p className="py-10 text-center text-sm text-muted">
          Nothing logged yet — a session is one day's setup; a multi-day session groups its days.
        </p>
      ) : (
        <div className="card divide-y divide-hairline">
          {items.map((item) =>
            item.multi ? (
              <SwipeRow key={`m${item.id}`} enabled={canTouch(me, item)} onDelete={() => deleteItem(item)}>
                <Link to={`/log/${item.id}`} className={'flex items-center gap-3 px-4 py-3 active:bg-grouped' + (item.status === 'wrapped' ? ' opacity-60' : '')}>
                  <Layers size={17} className="shrink-0 text-cyan" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="truncate text-sm font-medium text-ink">{item.name}</span>
                      <Chip kind={item.kind} />
                      {item.status === 'wrapped' && <span className="text-[10px] uppercase tracking-wide text-faint">wrapped</span>}
                    </div>
                    {/* The list orders by recency of EDIT, so the date shown
                        is the edit date — labeled, so nobody reads it as the
                        session date (Charles, 2026-08-05). */}
                    <div className="mt-0.5 truncate text-xs text-muted">
                      {item.sessionCount} day{item.sessionCount === 1 ? '' : 's'}
                      {item.updatedAt ? ` · Last edited ${fmtEditedDay(item.updatedAt)}` : ''}
                      {item.client ? ` · ${item.client}` : ''}
                    </div>
                  </div>
                  <ChevronRight size={16} className="shrink-0 text-faint" />
                </Link>
              </SwipeRow>
            ) : (
              <SwipeRow key={`s${item.id}`} enabled={canTouch(me, item)} onDelete={() => deleteItem(item)}>
                <Link to={`/log/s/${item.id}`} className="flex items-center gap-3 px-4 py-3 active:bg-grouped">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-ink">{item.label || 'Session'}</div>
                    <div className="mt-0.5 text-xs text-muted">
                      {fmtDay(item.date)}
                      {item.updatedAt && localDayOf(item.updatedAt) !== item.date
                        ? ` · Last edited ${fmtEditedDay(item.updatedAt)}`
                        : ''}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2.5 text-xs text-faint">
                    {item.gearCount > 0 && (
                      <span className="flex items-center gap-1"><Mic size={13} />{item.gearCount}</span>
                    )}
                    {item.photoCount > 0 && (
                      <span className="flex items-center gap-1"><Camera size={13} />{item.photoCount}</span>
                    )}
                    <Initials name={item.by} />
                  </div>
                  <ChevronRight size={16} className="shrink-0 text-faint" />
                </Link>
              </SwipeRow>
            )
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Search results, in the home feed's own row styles. Sessions carry a
 * `hit` line — the gear/chain/notes text that matched — so "U87" reads
 * as an answer, not just a list of days.
 */
function SearchResults({ search, q }) {
  const sessions = search.data?.sessions || []
  const projects = search.data?.projects || []

  if (search.isFetched && sessions.length === 0 && projects.length === 0) {
    return <p className="py-10 text-center text-sm text-muted">Nothing logged matches “{q}”.</p>
  }

  return <LogSearchSections sessions={sessions} projects={projects} />
}

/**
 * The two result sections on their own — shared with the hub's Search
 * screen (2026-09-13), which shows Session Log hits beside Information
 * hits and owns its own empty state. The Session Log home wraps them with
 * its own "nothing logged" message above.
 */
export function LogSearchSections({ sessions, projects }) {
  return (
    <div className="space-y-5">
      {projects.length > 0 && (
        <section>
          <h2 className="section-header mb-2">Multi-day sessions</h2>
          <div className="card divide-y divide-hairline">
            {projects.map((p) => (
              <Link key={p.id} to={`/log/${p.id}`} className="flex items-center gap-3 px-4 py-3 active:bg-grouped">
                <Layers size={17} className="shrink-0 text-cyan" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-medium text-ink">{p.name}</span>
                    <Chip kind={p.kind} />
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted">
                    {p.sessionCount} day{p.sessionCount === 1 ? '' : 's'}
                    {p.lastDate ? ` · ${fmtDay(p.lastDate)}` : ''}
                    {p.client ? ` · ${p.client}` : ''}
                  </div>
                </div>
                <ChevronRight size={16} className="shrink-0 text-faint" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {sessions.length > 0 && (
        <section>
          <h2 className="section-header mb-2">Sessions</h2>
          <div className="card divide-y divide-hairline">
            {sessions.map((s) => (
              <Link
                key={s.id}
                to={s.projectId ? `/log/${s.projectId}/s/${s.id}` : `/log/s/${s.id}`}
                className="flex items-center gap-3 px-4 py-3 active:bg-grouped"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-medium text-ink">{s.label || 'Session'}</span>
                    {s.projectName && <span className="shrink-0 text-xs text-faint">{s.projectName}</span>}
                  </div>
                  <div className="mt-0.5 truncate text-xs text-muted">
                    {fmtDay(s.date)}
                    {s.hit ? ` · ${s.hit}` : ''}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2.5">
                  <Initials name={s.by} />
                  <ChevronRight size={16} className="text-faint" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export function Chip({ kind }) {
  if (!kind || kind === 'other') return null
  const film = kind === 'film'
  return (
    <span
      className={
        'shrink-0 rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ' +
        (film ? 'border-film-fg text-film-fg' : 'border-music-fg text-music-fg')
      }
    >
      {kind}
    </span>
  )
}
